import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError, platformSettings as platformSettingsApi, type PlatformSettingsInfo } from '../api';
import { useAuth } from '../auth';

/**
 * SCO-105 — Platform Admin only. The one setting here (for now) is the homepage hero
 * image shown identically across every Unit's public site — a genuinely network-wide
 * value, unlike everything else on the Website settings page, which is per-Unit.
 */
export function PlatformSettings() {
  const { me } = useAuth();
  const [info, setInfo] = useState<PlatformSettingsInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    setError(null);
    platformSettingsApi
      .get()
      .then(setInfo)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  }, []);

  useEffect(() => {
    if (me?.isPlatformAdmin) load();
  }, [me, load]);

  if (!me?.isPlatformAdmin) return <Navigate to="/" replace />;

  async function onFileSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      setInfo(await platformSettingsApi.uploadHeroImage(file));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload image');
    } finally {
      setUploading(false);
    }
  }

  async function clear() {
    if (!confirm('Remove the hero image from every Unit site?')) return;
    setError(null);
    setUploading(true);
    try {
      setInfo(await platformSettingsApi.clearHeroImage());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove image');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="panel">
      <h1>Platform settings</h1>
      <p className="muted">
        Platform Admin only. These apply identically across every Unit's public website.
      </p>
      {error && <p className="error">{error}</p>}

      <h2>Homepage hero image</h2>
      <p className="muted small">
        Shown behind the Unit name on every Unit's public site homepage. Uploading replaces it
        everywhere immediately.
      </p>
      {info?.heroImageUrl && (
        <p>
          <img
            src={info.heroImageUrl}
            alt=""
            style={{ maxWidth: 320, borderRadius: 8, display: 'block' }}
          />
        </p>
      )}
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <label>
          {uploading ? 'Uploading…' : info?.heroImageUrl ? 'Replace image' : 'Upload image'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onFileSelected}
            disabled={uploading}
            style={{ display: 'block' }}
          />
        </label>
        {info?.heroImageUrl && !uploading && (
          <button type="button" onClick={clear}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
